import { Command } from '../model/job.model'

/**
 * Some system commands bundle multiple per-issue items into a single command's
 * `data` payload (e.g. Advance Resolve Errors). That bundling makes it hard for
 * the Audit team to screenshot per-issue evidence for KPMG tie-outs.
 *
 * For each configured command type, the transformer below splits a bundled
 * command into a parent row (unchanged, preserving the existing screenshot
 * baseline) and N synthetic child rows whose `data` contains only one item from
 * the bulk array. Children are rendered indented under the parent via
 * ag-Grid tree data.
 *
 * See PVAL-25510 for context.
 */
export interface BulkCommandSplitConfig {
  /** Matches `Command.type` from the /commandLogs endpoint. */
  type: string
  /** Path on `Command.data` whose value is the array to split on. */
  splitField: string
  /**
   * Path on each split-array item whose value should populate the child row's
   * `note` column. Lets auditors see the per-issue resolution note on the
   * child row without modifying the parent's note (PVAL-25412 handles that).
   */
  childNoteField?: string
}

/**
 * Active split configs. Add an entry per command type that bundles
 * per-issue data on the System Commands tab.
 *
 * Today only "Advance Resolve Errors" is bundled — Resolve and Annotate are
 * already expanded into per-issue rows upstream by perseus-worker (verified
 * against /commandLogs sample data, May 2026). Add more entries here when /
 * if other bulk types surface.
 */
export const BULK_COMMAND_SPLIT_CONFIGS: readonly BulkCommandSplitConfig[] = [
  {
    type: 'Advance Resolve Errors',
    splitField: 'errors',
    childNoteField: 'note',
  },
]

/** A row supplied to the grid. Carries the tree-data path. */
export interface BulkSplitRow extends Command {
  /**
   * Tree-data hierarchy path. Parents are `[parentId]`; children are
   * `[parentId, childId]`.
   */
  path: string[]
}

const CHILD_ID_SEPARATOR = '#'

function findConfig(type: string): BulkCommandSplitConfig | undefined {
  return BULK_COMMAND_SPLIT_CONFIGS.find(c => c.type === type)
}

function getField(
  obj: Record<string, unknown> | undefined,
  field: string,
): unknown {
  if (!obj) return undefined
  return obj[field]
}

/**
 * Pure transformer: turns the raw command list into the row list that the
 * grid consumes. Non-bulk commands pass through with `path: [rowId]`.
 * Bulk commands (per BULK_COMMAND_SPLIT_CONFIGS) expand into one parent row
 * plus N child rows with `path: [rowId, '${rowId}#${i}']`.
 */
export function expandBulkCommands(commands: Command[]): BulkSplitRow[] {
  const rows: BulkSplitRow[] = []

  commands.forEach((command, index) => {
    const parentId = `cmd-${index}`
    const config = findConfig(command.type)

    // No config for this type → pass-through, single row.
    if (!config) {
      rows.push({ ...command, path: [parentId] })
      return
    }

    const data = command.data as unknown as Record<string, unknown> | undefined
    const splitValue = getField(data, config.splitField)

    // Configured but no splittable array → pass-through. Defensive: real data
    // always has the array, but we don't want a malformed command to throw.
    if (!Array.isArray(splitValue) || splitValue.length === 0) {
      rows.push({ ...command, path: [parentId] })
      return
    }

    // Parent row: unchanged from input. Auditors who screenshot the bundled
    // command today see the same thing.
    rows.push({ ...command, path: [parentId] })

    // Children: clone `data`, replace the split-array with a single element,
    // and pull the per-item note onto the child's `note` column when
    // configured. All other Command fields are inherited verbatim so each
    // child row reads as a complete, standalone record.
    splitValue.forEach((item, itemIndex) => {
      const childData: Record<string, unknown> = {
        ...(data ?? {}),
        [config.splitField]: [item],
      }

      const itemRecord =
        item && typeof item === 'object'
          ? (item as Record<string, unknown>)
          : undefined
      const childNote =
        config.childNoteField !== undefined
          ? itemRecord?.[config.childNoteField]
          : undefined

      rows.push({
        ...command,
        data: childData as unknown as Command['data'],
        note: typeof childNote === 'string' ? childNote : command.note,
        path: [parentId, `${parentId}${CHILD_ID_SEPARATOR}${itemIndex}`],
      })
    })
  })

  return rows
}
